/**
 * CreateActionEditForm — inline edit form for the standalone CreateActionCard.
 *
 * Renders entity-specific editable fields for create-type CRM actions using the
 * same schema-driven inputs as the CRM create modal (via CreateActionFields),
 * and registers save/cancel handlers into ActionEditContext so ActionAccordion
 * can drive the Save and Cancel buttons.
 *
 * The field state/schema/dirty logic lives in useCreateActionFields so the
 * merged card (MergedCreateActionCard) can reuse it and compose the primary
 * fields with editable association targets under one combined save.
 */

import { useCallback, useEffect, useLayoutEffect } from 'react';

import type {
  ObjectCreatedEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import { useActionEdit } from './ActionAccordion';
import { CreateActionFields } from './CreateActionFields';
import { useActionPayloadSave } from './shared';
import { useCreateActionFields } from './useCreateActionFields';

interface CreateActionEditFormProps {
  action: ProposedAction<ObjectCreatedEntityPayload>;
  enrichedData: ActionRendererProps['enrichedData'];
  proposalId?: number;
  orgId?: number;
  onUpdate?: ActionRendererProps['onUpdate'];
}

export function CreateActionEditForm({
  action,
  proposalId,
  orgId,
  onUpdate,
}: CreateActionEditFormProps) {
  const { setDirty, saveHandlerRef, cancelHandlerRef } = useActionEdit();
  const { savePayload } = useActionPayloadSave({
    orgId,
    proposalId,
    actionId: action.id,
    onUpdate,
  });

  const {
    fieldKeys,
    values,
    schema,
    handleChange,
    isDirty,
    buildPayload,
    reset,
  } = useCreateActionFields(action, orgId);

  // Propagate dirty state up to the accordion.
  useEffect(() => {
    setDirty(isDirty);
  }, [isDirty, setDirty]);

  const save = useCallback(async () => {
    await savePayload(buildPayload());
  }, [buildPayload, savePayload]);

  // Update refs after every render so the accordion always has the latest handlers.
  useLayoutEffect(() => {
    saveHandlerRef.current = save;
    cancelHandlerRef.current = reset;
  });

  if (fieldKeys.length === 0) {
    return (
      <div className='px-4 py-3 text-sm text-fg-tertiary'>
        No editable fields for this object type.
      </div>
    );
  }

  return (
    <div className='px-4 py-3'>
      <CreateActionFields
        fieldKeys={fieldKeys}
        values={values}
        schema={schema}
        onChange={handleChange}
      />
    </div>
  );
}
